/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { type ReactNode, type CSSProperties } from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

/** Visual variant for form section headings and field gaps. */
export type FormSectionVariant = 'default' | 'quiet';

interface FormSectionProps {
  title?: string;
  children: ReactNode;
  divider?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  /** Quiet uses meta eyebrow typography and denser field gaps. */
  variant?: FormSectionVariant;
}

/**
 * Groups form fields under an optional titled section with collapse support.
 *
 * @param props - Section configuration.
 * @returns Section element wrapping children.
 */
export const FormSection: React.FC<FormSectionProps> = ({
  title,
  children,
  divider = false,
  collapsed = false,
  onToggle,
  variant = 'default',
}) => {
  const isQuiet = variant === 'quiet';

  const dividerStyle: CSSProperties = {
    height: 1,
    background: 'var(--border-subtle)',
    border: 'none',
    margin: 'var(--space-3) 0',
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: onToggle ? 'pointer' : undefined,
    marginBottom: collapsed ? 0 : isQuiet ? undefined : 'var(--space-4)',
  };

  const toggleStyle: CSSProperties = {
    fontSize: 'var(--text-body-sm)',
    color: 'var(--seed-base)',
    transition: 'var(--transition-standard)',
    transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
    width: 'var(--space-6)',
    height: 'var(--space-6)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--surface-tertiary)',
  };

  const defaultTitleStyle: CSSProperties = {
    fontSize: 'var(--text-app-section)',
    fontWeight: 'var(--font-semibold)',
    lineHeight: 'var(--leading-snug)',
    letterSpacing: 'var(--tracking-tight)',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-family)',
    margin: 0,
  };

  // Quiet section > field labels: --text-body-sm / secondary above --text-small / tertiary
  const quietTitleClass = cn(
    'm-0',
    'font-[family-name:var(--font-family)]',
    'font-[weight:var(--font-semibold)]',
    'text-[length:var(--text-body-sm)]',
    'leading-[var(--leading-snug)]',
    'tracking-[var(--tracking-wide)]',
    'text-(--text-secondary)',
  );

  const fieldsClass = cn(
    'flex flex-col',
    isQuiet ? 'gap-(--space-3)' : undefined,
  );

  const fieldsStyle: CSSProperties | undefined = isQuiet
    ? undefined
    : { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' };

  return (
    <section
      data-testid="form-section"
      data-variant={variant}
      className={cn('flex flex-col', isQuiet && 'gap-(--space-2)')}
    >
      {divider && <hr style={dividerStyle} />}
      {title && (
        <div
          style={headerStyle}
          className={isQuiet && !collapsed ? 'mb-(--space-2)' : undefined}
          onClick={onToggle}
          role={onToggle ? 'button' : undefined}
          aria-expanded={onToggle ? !collapsed : undefined}
          data-testid={`section-header-${title.toLowerCase().replace(/\s/g, '-')}`}
        >
          <h3 className={isQuiet ? quietTitleClass : undefined} style={isQuiet ? undefined : defaultTitleStyle}>
            {title}
          </h3>
          {onToggle && <span style={toggleStyle}>▾</span>}
        </div>
      )}
      {!collapsed && (
        <div className={fieldsClass} style={fieldsStyle}>
          {children}
        </div>
      )}
    </section>
  );
};
